import React, { createContext, useContext, useState, useCallback } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';

type ToastVariant = 'success' | 'danger' | 'warning' | 'info';

interface ToastMessage {
    id: number;
    message: string;
    variant: ToastVariant;
}

interface ToastContextType {
    showToast: (message: string, variant?: ToastVariant) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

let toastId = 0;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<ToastMessage[]>([]);

    const showToast = useCallback((message: string, variant: ToastVariant = 'success') => {
        const id = ++toastId;
        setToasts(prev => [...prev, { id, message, variant }]);
    }, []);

    const removeToast = useCallback((id: number) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const getVariantStyles = (variant: ToastVariant) => {
        switch (variant) {
            case 'success':
                return { bg: '#d4edda', border: '#c3e6cb', text: '#155724' };
            case 'danger':
                return { bg: '#f8d7da', border: '#f5c6cb', text: '#721c24' };
            case 'warning':
                return { bg: '#fff3cd', border: '#ffeeba', text: '#856404' };
            case 'info':
                return { bg: '#d1ecf1', border: '#bee5eb', text: '#0c5460' };
        }
    };

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <ToastContainer
                position="top-center"
                className="p-3"
                style={{ zIndex: 9999, position: 'fixed' }}
            >
                {toasts.map(toast => {
                    const styles = getVariantStyles(toast.variant);
                    return (
                        <Toast
                            key={toast.id}
                            onClose={() => removeToast(toast.id)}
                            delay={4000}
                            autohide
                            style={{
                                backgroundColor: styles.bg,
                                borderColor: styles.border
                            }}
                        >
                            <Toast.Body style={{ color: styles.text }}>
                                {toast.message}
                            </Toast.Body>
                        </Toast>
                    );
                })}
            </ToastContainer>
        </ToastContext.Provider>
    );
};

export const useToast = (): ToastContextType => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};
